import { PaymentRequestPaymentMethodEvent, Stripe, StripeElements, SetupIntentResult } from '@stripe/stripe-js';
import { ApolloClient, ApolloError, NormalizedCacheObject } from '@apollo/client';
import { updateResponse } from 'api/data/response';
import { apolloErrorFormatter, pickKeys } from 'utils';
import { processCardWalletSetupIntent, setupIntentDataVar } from '../stripe';
import { createSetupIntentMutation, ProcessPaymentInput, processPaymentMutation } from './mutation';
import * as ResponseQuery from 'graphql/response.graphql';
import { Response } from 'api/data/response/types';
import {
  PaymentErrorType,
  PaymentMethodSource,
  PaymentMethodType,
  ProcessPaymentData,
  SetupIntentInput,
} from './types';

const API = {
  processPayment: async (
    {
      paymentMethodId,
      setupIntentResult,
      customerId,
      paymentMethodSource,
      paymentMethodType,
      financialConnectionsAccountId,
      response,
      sessionKey,
    }: ProcessPaymentData,
    client: ApolloClient<NormalizedCacheObject>,
  ): Promise<{ succeeded: boolean; clientSecret?: string }> => {
    if (isOkToProcessPayment({ paymentMethodSource, paymentMethodType, paymentMethodId, setupIntentResult })) {
      try {
        const { error } = setupIntentResult ?? { error: null };

        const processPaymentInput: ProcessPaymentInput = {
          responseId: response.id ?? '',
          customerId: customerId,
          paymentMethodId: paymentMethodId ?? null,
          financialConnectionsAccountId: financialConnectionsAccountId ?? null,
          paymentMethodType: paymentMethodType,
          paymentMethodSource: paymentMethodSource,
          sessionKey,
          error: error?.type
            ? {
                type: error?.type,
                code: error?.code,
                message: error?.message,
              }
            : null,
        };

        const { data } = await processPaymentMutation(processPaymentInput, client);
        const responseStatus = data?.processPayment.response.status;
        const orderStatus = data?.processPayment.response.order.status;

        updateResponse({
          status: responseStatus,
          order: { ...response.order, status: orderStatus },
        });

        if (setupIntentResult?.error?.message) throw new Error(setupIntentResult.error.message);
        if (data?.processPayment.failureMessage) throw new Error(data?.processPayment.failureMessage);
        //when tap to pay, "succeeded" will always return false because the payment status will be requires_payment_method
        return {
          succeeded: data?.processPayment.status === 'SUCCEEDED',
          clientSecret: data?.processPayment.clientSecret,
        };
      } catch (error) {
        throw new Error(apolloErrorFormatter(error as ApolloError));
      }
    } else {
      throw new Error('Something went wrong, try again later');
    }
  },
};

type isOkToProcessPaymentArgs = {
  paymentMethodSource: PaymentMethodSource | undefined | null;
  paymentMethodType: PaymentMethodType;
  paymentMethodId: string | undefined | null;
  setupIntentResult: SetupIntentResult | undefined | null;
};
const isOkToProcessPayment = ({
  paymentMethodSource,
  paymentMethodType,
  paymentMethodId,
  setupIntentResult,
}: isOkToProcessPaymentArgs) => {
  const isTerminal = paymentMethodSource === PaymentMethodSource.PAYMENT_TERMINAL;
  const isOffline = paymentMethodType === PaymentMethodType.OFFLINE;
  const isTapToPay = paymentMethodSource === PaymentMethodSource.TAP_TO_PAY;
  const intentSucceeded = setupIntentResult?.setupIntent?.status === 'succeeded';
  const intentWithError = !!setupIntentResult?.error;

  return isTerminal || isOffline || isTapToPay || paymentMethodId || intentSucceeded || intentWithError;
};

export const saveResponseAndProcessPayment = async (
  client: ApolloClient<NormalizedCacheObject>,
  response: Response,
  processPaymentData: ProcessPaymentData,
) => {
  try {
    if (response.status !== 'PROCESSED') {
      await client.mutate({
        mutation: ResponseQuery.SaveResponse,
        variables: {
          response: {
            ...pickKeys(response, 'id', 'pageId'),
            status: 'SUBMITTED',
            paymentMethodType: processPaymentData.paymentMethodType,
            user: response.user && pickKeys(response.user, 'fullName', 'email'),
          },
        },
      });
    }
    return await API.processPayment(processPaymentData, client);
  } catch (error) {
    throw new Error(apolloErrorFormatter(error as ApolloError));
  }
};

export const fetchOrProcessSetupIntentData = async (
  method: PaymentMethodType,
  stripe: Stripe | null,
  client: ApolloClient<NormalizedCacheObject>,
  response: Response,
  cardPaymentMethod: StripeElements | PaymentRequestPaymentMethodEvent | null,
) => {
  if (response.status !== 'PROCESSED' && response.pageId)
    await client.mutate({
      mutation: ResponseQuery.SaveResponse,
      variables: {
        response: {
          ...pickKeys(response, 'id', 'pageId'),
          status: 'SUBMITTED',
          user: response.user && pickKeys(response.user, 'fullName', 'email'),
        },
      },
    });

  if (method === PaymentMethodType.ACH) {
    const { paymentMethodDetails, setupIntentResult: setupIntentResultVar } = setupIntentDataVar();
    return {
      paymentMethodId: paymentMethodDetails.paymentMethodId,
      setupIntentResult: setupIntentResultVar,
      customerId: paymentMethodDetails.customerId,
      financialConnectionsAccountId: paymentMethodDetails.financialConnectionsAccountId,
      paymentMethodType: method,
      response,
    };
  }

  if (method === PaymentMethodType.OFFLINE) {
    return {
      paymentMethodType: PaymentMethodType.OFFLINE,
      response,
    };
  }

  const { setupIntentResult, paymentMethodId, customerId } = await processCardWalletSetupIntent(
    method,
    stripe,
    client,
    cardPaymentMethod,
    response.user,
    response.id,
  );
  return {
    paymentMethodId,
    setupIntentResult,
    customerId,
    paymentMethodType: PaymentMethodType.CARD,
    response,
  };
};

export const processPayment = async (
  method: PaymentMethodType,
  stripe: Stripe | null,
  client: ApolloClient<NormalizedCacheObject>,
  response: Response,
  cardPaymentMethod: StripeElements | PaymentRequestPaymentMethodEvent | null,
) => {
  try {
    const processPaymentData = await fetchOrProcessSetupIntentData(method, stripe, client, response, cardPaymentMethod);
    return await saveResponseAndProcessPayment(client, response, processPaymentData);
  } catch (error) {
    throw new Error((error as PaymentErrorType).message);
  }
};

export const createSetupIntent = async (
  setupIntentInput: SetupIntentInput,
  client: ApolloClient<NormalizedCacheObject>,
) => {
  try {
    const { data } = await createSetupIntentMutation(setupIntentInput, client);
    const paymentMethodId = data?.createSetupIntent?.paymentMethodId || '';
    const clientSecret = data?.createSetupIntent?.clientSecret || '';
    const customerId = data?.createSetupIntent.customerId || '';

    return { paymentMethodId, customerId, clientSecret };
  } catch (error) {
    throw new Error(apolloErrorFormatter(error as ApolloError));
  }
};
